import {
  cardDividerBorderClass,
  cardMutedTextClass,
  cardTitleTextClass,
  compactInputClass,
  ghostBtnClass,
  salesIndigoSurfaceCardClass,
  secondaryBtnClass,
} from "@/components/common/surfaceClasses";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import type { RoleplayOrgScenarioRow } from "@/lib/training/trainingApiClient";

type Props = {
  row: RoleplayOrgScenarioRow;
  index: number;
  saving: boolean;
  busy: "suggest" | "qa" | null;
  onClose: () => void;
  publishRowAt: (index: number) => Promise<void>;
  unpublishRowAt: (index: number) => Promise<void>;
  saveRow: (row: RoleplayOrgScenarioRow) => Promise<void>;
  runSuggestQa: (scenarioKey: string) => Promise<void>;
  deleteRow: (id: string) => Promise<void>;
  removeRowAt: (index: number) => void;
  patchRowAt: (index: number, patch: Partial<RoleplayOrgScenarioRow>) => void;
};

export function RoleplayScenarioEditorPanel({
  row,
  index,
  saving,
  busy,
  onClose,
  publishRowAt,
  unpublishRowAt,
  saveRow,
  runSuggestQa,
  deleteRow,
  removeRowAt,
  patchRowAt,
}: Props) {
  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "ta-scenario-editor" }} className={`${salesIndigoSurfaceCardClass} space-y-3 text-sm shadow-sm`}>
      <div className={`flex flex-wrap items-start justify-between gap-2 border-b pb-2 ${cardDividerBorderClass}`}>
        <div className="min-w-0 flex-1">
          <div className={`font-semibold truncate ${cardTitleTextClass}`}>{row.label || "(no label)"}</div>
          <div className={`text-xs font-mono truncate ${cardMutedTextClass}`}>{row.scenario_key}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <span
            className={
              "text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 " +
              (row.is_active ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-200" : "bg-gray-200 text-gray-700 dark:bg-zinc-700 dark:text-zinc-300")
            }
          >
            {row.is_active ? "Published" : "Draft"}
          </span>
          {row.is_sharable ? (
            <span className="text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
              Sharable
            </span>
          ) : null}
          <button
            type="button"
            className={`text-xs px-2 py-1 font-semibold ${ghostBtnClass}`}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          {...{ [FIREFLY_TARGET_ATTR]: "ta-publish-scenario" }}
          className="text-xs px-2 py-1.5 rounded-lg bg-emerald-600 text-white font-semibold disabled:opacity-40 disabled:pointer-events-none"
          disabled={saving || row.is_active}
          onClick={() => void publishRowAt(index)}
        >
          Publish
        </button>
        <button
          type="button"
          className={`text-xs px-2 py-1.5 font-semibold ${secondaryBtnClass}`}
          disabled={saving || !row.is_active}
          onClick={() => void unpublishRowAt(index)}
        >
          Unpublish
        </button>
        <button
          type="button"
          className="text-xs px-2 py-1.5 rounded-lg bg-indigo-600 text-white font-semibold disabled:opacity-50"
          disabled={saving}
          onClick={() => void saveRow(row)}
        >
          Save changes
        </button>
        <button
          type="button"
          className={`text-xs px-2 py-1.5 font-semibold disabled:opacity-50 ${secondaryBtnClass}`}
          disabled={busy === "qa" || saving}
          onClick={() => void runSuggestQa(row.scenario_key.trim())}
        >
          Suggest QA from KB
        </button>
        {row.id ? (
          <button
            type="button"
            className="text-xs px-2 py-1.5 rounded-lg border border-red-200 text-red-700 font-semibold disabled:opacity-50 dark:border-red-800 dark:text-red-400"
            disabled={saving}
            onClick={() => void deleteRow(row.id)}
          >
            Delete
          </button>
        ) : (
          <button type="button" className={`text-xs px-2 py-1.5 font-semibold ${ghostBtnClass}`} onClick={() => removeRowAt(index)}>
            Remove draft
          </button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        <label className={`block text-xs ${cardTitleTextClass}`}>
          Key (slug)
          <input
            className={`mt-0.5 w-full text-xs font-mono ${compactInputClass}`}
            value={row.scenario_key}
            onChange={(e) => patchRowAt(index, { scenario_key: e.target.value })}
          />
        </label>
        <label className={`block text-xs ${cardTitleTextClass}`}>
          Sort order
          <input
            type="number"
            className={`mt-0.5 w-full text-xs ${compactInputClass}`}
            value={row.sort_order}
            onChange={(e) => patchRowAt(index, { sort_order: Math.round(Number(e.target.value)) || 0 })}
          />
        </label>
      </div>
      <label className={`block text-xs ${cardTitleTextClass}`}>
        Label
        <input className={`mt-0.5 w-full text-xs ${compactInputClass}`} value={row.label} onChange={(e) => patchRowAt(index, { label: e.target.value })} />
      </label>
      <label className={`block text-xs ${cardTitleTextClass}`}>
        Persona
        <textarea
          className={`mt-0.5 w-full text-xs min-h-[72px] ${compactInputClass}`}
          value={row.persona}
          onChange={(e) => patchRowAt(index, { persona: e.target.value })}
        />
      </label>
      <label className={`block text-xs ${cardTitleTextClass}`}>
        KB context digest (optional; used when freeze digest is on)
        <textarea
          className={`mt-0.5 w-full text-xs min-h-[56px] ${compactInputClass}`}
          value={row.kb_context_digest ?? ""}
          onChange={(e) => patchRowAt(index, { kb_context_digest: e.target.value || null })}
        />
      </label>
      <div className="flex flex-wrap gap-4 text-xs">
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={row.is_active} onChange={(e) => patchRowAt(index, { is_active: e.target.checked })} />
          Active (same as Publish when saved)
        </label>
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={row.is_sharable} onChange={(e) => patchRowAt(index, { is_sharable: e.target.checked })} />
          Sharable (manufacturer orgs: subscribers can inherit when enabled)
        </label>
      </div>
      <details className="text-xs">
        <summary className={`cursor-pointer font-semibold ${cardTitleTextClass}`}>Trainer rubric &amp; QA material</summary>
        <div className="mt-2 space-y-2">
          <label className={`block ${cardTitleTextClass}`}>
            Rubric (markdown)
            <textarea
              className={`mt-0.5 w-full text-xs min-h-[56px] ${compactInputClass}`}
              value={row.trainer_rubric_markdown ?? ""}
              onChange={(e) => patchRowAt(index, { trainer_rubric_markdown: e.target.value || null })}
            />
          </label>
          <label className={`block ${cardTitleTextClass}`}>
            Ideal answer outline
            <textarea
              className={`mt-0.5 w-full text-xs min-h-[48px] ${compactInputClass}`}
              value={row.ideal_answer_outline ?? ""}
              onChange={(e) => patchRowAt(index, { ideal_answer_outline: e.target.value || null })}
            />
          </label>
          <label className={`block ${cardTitleTextClass}`}>
            Example questions (one per line → stored as JSON array on save)
            <textarea
              className={`mt-0.5 w-full text-xs min-h-[48px] ${compactInputClass}`}
              value={Array.isArray(row.example_questions) ? row.example_questions.map((x) => String(x)).join("\n") : ""}
              onChange={(e) =>
                patchRowAt(index, {
                  example_questions: e.target.value
                    .split("\n")
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
            />
          </label>
        </div>
      </details>
    </div>
  );
}
